import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ShareModule } from '../../../share.module';
import { NoteService } from '../../../services/note.service';
import { DataService } from '../../../services/data.service';
import { NoteDto } from '../../../../models/note.model';
import { Notification } from '../../../notification/notification.component';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';

TimeAgo.addDefaultLocale(en);
const timeAgo = new TimeAgo('en-US');

@Component({
  selector: 'app-filter-criteria-group-notes',
  standalone: true,
  imports: [ShareModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './filter-criteria-group-notes.html',
  styleUrl: './filter-criteria-group-notes.scss',
})
export class FilterCriteriaGroupNotes implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() groupIndex = 0;
  @Input() groupName = '';
  /** Stable, position-independent id for the group. Falls back to the index when absent. */
  @Input() groupId = '';

  @ViewChild('overlayHost') overlayHost?: ElementRef<HTMLElement>;

  @Output() addNote = new EventEmitter<number>();
  @Output() duplicateWithoutNotes = new EventEmitter<number>();
  @Output() deleteGroup = new EventEmitter<number>();
  @Output() notify = new EventEmitter<Notification>();

  showNotesMenu = false;
  showNoteCard = false;
  isNoteFullscreen = false;
  showBoxShadow = false;
  showDeleteNotesModal = false;
  isDeletingGroupNotes = false;
  noteTitle = 'Add Note';
  noteText = '';

  notes: NoteDto[] = [];
  noteCount = 0;
  isLoadingNotes = false;
  isSavingNote = false;
  editingNoteGuid: string | null = null;
  activeNoteMenuGuid: string | null = null;

  /** Fixed-position anchor for the compact card (escapes ancestor overflow clipping). */
  cardTop = 0;
  cardRight = 0;

  constructor(
    private readonly noteService: NoteService,
    private readonly dataService: DataService,
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.loadNoteCount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const idChange = changes['groupId'];
    if (idChange && !idChange.firstChange) {
      this.loadNoteCount();
    }
  }

  ngAfterViewInit(): void {
    // Move the overlay host to document.body so the popover/card escape the
    // sticky group-header stacking context and render above the filter rows.
    if (this.overlayHost?.nativeElement) {
      this.renderer.appendChild(document.body, this.overlayHost.nativeElement);
    }
  }

  /** Close the note card when clicking outside of it. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showNoteCard) {
      return;
    }

    const target = event.target as Node;
    const clickedInsideOverlay = !!this.overlayHost?.nativeElement.contains(target);
    const clickedTrigger = this.el.nativeElement.contains(target);

    if (!clickedInsideOverlay && !clickedTrigger) {
      this.closeNoteCard();
    }
  }

  ngOnDestroy(): void {
    if (this.overlayHost?.nativeElement?.parentNode) {
      this.renderer.removeChild(
        this.overlayHost.nativeElement.parentNode,
        this.overlayHost.nativeElement,
      );
    }
  }

  private get filterGuid(): string {
    return this.dataService.selectedGUID;
  }

  private get groupKey(): string {
    return this.groupId || String(this.groupIndex);
  }

  onNoteTextChanged(event: any): void {
    this.noteText = event?.detail ?? event?.target?.value ?? '';
  }

  onEnterPressed(event: Event): void {
    event.preventDefault();
    if (!this.disableSendButton()) {
      this.onSaveNote();
    }
  }

  onSaveNote(): void {
    const content = this.noteText.trim();
    console.log('onSaveNote guard check:', {
      content,
      isSavingNote: this.isSavingNote,
      filterGuid: this.filterGuid,
      groupId: this.groupKey,
    });
    if (!content || this.isSavingNote || !this.filterGuid) {
      return;
    }

    this.isSavingNote = true;

    if (this.editingNoteGuid) {
      const noteGuid = this.editingNoteGuid;
      this.noteService.updateNote(noteGuid, content).subscribe({
        next: () => {
          this.resetEditor();
          this.loadNotes();
        },
        error: () => {
          this.isSavingNote = false;
        },
      });
    } else {
      this.noteService.addNote(this.filterGuid, this.groupKey, content).subscribe({
        next: () => {
          this.resetEditor();
          this.loadNotes();
        },
        error: () => {
          this.isSavingNote = false;
        },
      });
    }
  }

  private resetEditor(): void {
    this.noteText = '';
    this.editingNoteGuid = null;
    this.isSavingNote = false;
  }

  disableSendButton(): boolean {
    return !this.noteText?.trim() || this.isSavingNote;
  }

  toggleNotesMenu(): void {
    this.showNotesMenu = !this.showNotesMenu;
  }

  closeNotesMenu(): void {
    this.showNotesMenu = false;
  }

  onAddNote(): void {
    if (!this.filterGuid) {
      this.notify.emit({} as Notification);
      setTimeout(() => {
        this.notify.emit({
          label: 'Error',
          description: 'Please save the filter before adding a note.',
          type: 'error',
        });
      });
      return;
    }
    this.showNoteCard = true;
    this.positionCard();
    this.addNote.emit(this.groupIndex);
    // Clear cached notes before loading fresh notes
    this.notes = [];
    this.loadNotes();
  }

  openNotes(event?: Event): void {
    // Prevent this click from reaching the document:click handler, which would
    // otherwise treat the trigger (rendered in the parent component) as an
    // outside click and immediately close the card.
    event?.stopPropagation();
    this.showNoteCard = true;
    this.positionCard();
    // Clear cached notes before loading fresh notes
    this.notes = [];
    this.loadNotes();
  }

  /** Position the floating card just below the notes trigger, aligned to its right edge. */
  private positionCard(): void {
    const rect = this.el.nativeElement.getBoundingClientRect();
    this.cardTop = rect.bottom + 4;
    this.cardRight = Math.max(window.innerWidth - rect.right, 8);
  }

  loadNotes(): void {
    if (!this.filterGuid) {
      this.notes = [];
      this.noteCount = 0;
      return;
    }

    this.isLoadingNotes = true;
    this.noteService.getNotes(this.filterGuid, this.groupKey).subscribe({
      next: (response) => {
        this.notes = response?.notes ?? [];
        this.noteCount = this.notes.length;
        this.isLoadingNotes = false;
      },
      error: () => {
        this.notes = [];
        this.isLoadingNotes = false;
      },
    });
  }

  loadNoteCount(): void {
    if (!this.filterGuid) {
      this.noteCount = 0;
      return;
    }

    this.noteService.getNoteCounts(this.filterGuid).subscribe({
      next: (response) => {
        const match = (response?.counts ?? []).find(
          (c) => String(c.groupId) === this.groupKey,
        );
        this.noteCount = match?.count ?? 0;
      },
      error: () => {
        this.noteCount = 0;
      },
    });
  }

  toggleNoteFullscreen(): void {
    this.isNoteFullscreen = !this.isNoteFullscreen;
  }

  closeNoteCard(): void {
    this.showNoteCard = false;
    this.isNoteFullscreen = false;
    this.resetEditor();
  }

  toggleNoteMenu(noteGuid: string): void {
    this.activeNoteMenuGuid =
      this.activeNoteMenuGuid === noteGuid ? null : noteGuid;
  }

  closeNoteMenu(): void {
    this.activeNoteMenuGuid = null;
  }

  onEditNote(note: NoteDto): void {
    this.editingNoteGuid = note.guid;
    this.noteText = note.content ?? '';
    this.closeNoteMenu();
  }

  onDeleteNote(note: NoteDto): void {
    this.closeNoteMenu();
    if (!note?.guid) {
      return;
    }

    this.noteService.deleteNote(note.guid).subscribe({
      next: () => {
        if (this.editingNoteGuid === note.guid) {
          this.resetEditor();
        }
        this.loadNotes();
      },
    });
  }

  /** Delete-all is offered only to the owner of the group's notes. */
  get canDeleteGroupNotes(): boolean {
    return this.notes.some((note) => note.isOwner);
  }

  /** Open the confirmation modal before deleting all of the group's notes. */
  onDeleteGroupNotes(): void {
    if (!this.filterGuid || !this.canDeleteGroupNotes) {
      return;
    }
    this.showDeleteNotesModal = true;
  }

  cancelDeleteGroupNotes(): void {
    if (this.isDeletingGroupNotes) {
      return;
    }
    this.showDeleteNotesModal = false;
  }

  confirmDeleteGroupNotes(): void {
    if (!this.filterGuid || !this.canDeleteGroupNotes || this.isDeletingGroupNotes) {
      return;
    }

    this.isDeletingGroupNotes = true;
    this.noteService.deleteGroupNotes(this.filterGuid, this.groupKey).subscribe({
      next: () => {
        this.resetEditor();
        this.notes = [];
        this.noteCount = 0;
        this.isDeletingGroupNotes = false;
        this.showDeleteNotesModal = false;
        this.closeNoteCard();
      },
      error: () => {
        this.isDeletingGroupNotes = false;
      },
    });
  }

  getInitials(name: string): string {
    if (!name) {
      return '';
    }
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? parts.at(-1)!.charAt(0) : '';
    return (first + last).toUpperCase();
  }

  getRelativeTime(dateValue: string | null | undefined): string {
    if (!dateValue) {
      return '';
    }
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return timeAgo.format(date);
  }

  /** Exact timestamp for the tooltip, e.g. "31.12.2020, 13:30". */
  getExactTime(dateValue: string | null | undefined): string {
    if (!dateValue) {
      return '';
    }
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  trackByNoteGuid(_index: number, note: NoteDto): string {
    return note.guid;
  }

  onDuplicateWithoutNotes(): void {
    this.duplicateWithoutNotes.emit(this.groupIndex);
  }

  onDeleteGroup(): void {
    this.deleteGroup.emit(this.groupIndex);
  }
}
